import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router';

import request from '#api/axios';

import { PrevHeaderBarWithTitleTemplate } from '#templates';

import QualificationDegree from 'src/refactor_components/qualification/qualification-degree';
import QualificationCareer from 'src/refactor_components/qualification/qualification-career';
import QualificationEducation from 'src/refactor_components/qualification/qualification-education';
import QualificationCertificate from 'src/refactor_components/qualification/qualification-certificate';
import QualificationAward from 'src/refactor_components/qualification/qualification-award';

import { useToast } from '#providers/ToastProvider';
import useBranch from 'src/refactor_shared/hooks/useBranch';
import useFunnelFormProvider from 'src/refactor_shared/hooks/useFormProvider';

const Qualification = () => {
  const { id } = useParams();
  const { state } = useLocation();

  const { showToast } = useToast();
  const { Trunk, Branch } = useBranch('wait');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['qualification', state],
    queryFn: async () => {
      const res = await request<object>({
        method: 'get',
        url: `/${id}/${state}`
      });
      return res.data;
    },
    enabled: !!state && !!id
  });

  const { mutate } = useMutation({
    mutationFn: async (payload: any) =>
      await request({
        method: state ? 'patch' : 'post',
        url: state ? `${id}/${state}` : `${id}`,
        data: payload
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['qualification', state]);
      queryClient.invalidateQueries({
        predicate: query => query.queryKey[0] === 'MYACCOUNT'
      });
      showToast({
        message: `${PARSE_TITLE[id ? id : '']} 정보를 ${
          state ? '수정' : '등록'
        }하였습니다.`
      });
      navigate(`/profile`);
    },
    onError: () => {
      showToast({
        type: 'fail',
        message: `일시적인 오류입니다. 다시 시도해주세요.`
      });
    }
  });

  const onSubmit = (data: any) => {
    const { images, ...rest } = data;
    const isValidImageId = images?.length && images[0].id;

    mutate({
      ...rest,
      issuerId: data.issuerId ? data.issuerId : null,
      fileId: isValidImageId ? isValidImageId : ''
    });
  };

  const { FunnelFormProvider } = useFunnelFormProvider({
    onSubmit: onSubmit,
    data: data
  });

  return (
    <>
      <FunnelFormProvider>
        <PrevHeaderBarWithTitleTemplate
          title={PARSE_TITLE[id ? id : '']}
          defaultBackground
          rightContents={
            <button className="text-[#5C576B]">
              {state ? '수정' : '저장'}
            </button>
          }
        >
          <div className="flex flex-col p-20 gap-32">
            <Trunk branchTarget={id}>
              <Branch children={<>로딩중</>} branchName="wait" />
              <Branch children={<QualificationDegree />} branchName="degrees" />
              <Branch children={<QualificationCareer />} branchName="careers" />
              <Branch
                children={<QualificationEducation />}
                branchName="educations"
              />
              <Branch
                children={<QualificationCertificate />}
                branchName="licenses"
              />
              <Branch children={<QualificationAward />} branchName="awards" />
            </Trunk>
          </div>
        </PrevHeaderBarWithTitleTemplate>
      </FunnelFormProvider>
    </>
  );
};

export default Qualification;

const PARSE_TITLE: { [key: string]: string } = {
  '': '',
  degrees: '학력',
  careers: '경력',
  licenses: '자격증',
  educations: '교육',
  awards: '수상'
};
